/**
 * @fileoverview AR-924 — The task as the teacher posted it: title, due date, description and files.
 */

import { Anchor, Group, Stack, Text, TypographyStylesProvider } from '@mantine/core'
import { IconFile, IconMusic, IconPhoto, IconVideo } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import type { TaskTrackDeliverableFile, TaskTrackDeliverableTask } from '../../types'
import { formatNumericDate } from './dateFormat'

const FILE_ICON_SIZE = 14
const FILE_ICON_GAP = 6
const FILE_LIST_GAP = 4
/** Figma: 18px bold task title. */
const TASK_TITLE_SIZE = 18
const DESCRIPTION_STYLE = {
  fontSize: '0.875rem',
  '& div > :first-of-type': { marginTop: 0 },
  '& div > :last-child': { marginBottom: 0 },
}

/** Rich-text editors leave empty paragraphs at the end; they would push the next section down. */
function trimTrailingEmptyParagraphs(html: string): string {
  return html.replace(/(<p>(\s|&nbsp;|<br\s*\/?>)*<\/p>\s*)+$/i, '')
}

function FileIcon({ fileType }: { fileType: string }) {
  if (fileType === 'image') return <IconPhoto size={FILE_ICON_SIZE} />
  if (fileType === 'video') return <IconVideo size={FILE_ICON_SIZE} />
  if (fileType === 'audio') return <IconMusic size={FILE_ICON_SIZE} />
  return <IconFile size={FILE_ICON_SIZE} />
}

function InstructionFileLink({ file }: { file: TaskTrackDeliverableFile }) {
  return (
    <Anchor href={file.downloadUrl} target="_blank" rel="noopener noreferrer" size="sm">
      <Group spacing={FILE_ICON_GAP} noWrap>
        <FileIcon fileType={file.fileType} />
        <Text size="sm" lineClamp={1}>{file.name}</Text>
      </Group>
    </Anchor>
  )
}

export function DeliverableTaskHeader({ task }: { task: TaskTrackDeliverableTask }) {
  const { t, i18n } = useTranslation()
  return (
    <Stack spacing="xs">
      <Text size={TASK_TITLE_SIZE} weight={700}>{task.title}</Text>
      {task.date && (
        <Text size="sm">
          <Text span color="dimmed">{t('task_tracker.deliverable_drawer.due_date')}: </Text>
          <Text span weight={600}>{formatNumericDate(task.date.slice(0, 10), i18n.language)}</Text>
        </Text>
      )}
      {task.description && (
        <TypographyStylesProvider
          // The HTML sits inside a wrapper div, so the first/last paragraph is one level down.
          sx={DESCRIPTION_STYLE}
        >
          <div dangerouslySetInnerHTML={{ __html: trimTrailingEmptyParagraphs(task.description) }} />
        </TypographyStylesProvider>
      )}
      {task.files.length > 0 && (
        <Stack spacing={FILE_LIST_GAP}>
          <Text size="sm" weight={600}>{t('task_tracker.deliverable_drawer.instructions')}</Text>
          {task.files.map(file => <InstructionFileLink key={file.id} file={file} />)}
        </Stack>
      )}
    </Stack>
  )
}
